import { useCallback, useEffect, useState } from 'react';
import { Alerts } from './Alerts.tsx';
import { Audit } from './admin/Audit.tsx';
import { Estate } from './admin/Estate.tsx';
import { Sweep } from './admin/Sweep.tsx';
import { api, can, isSignedOut, type Capability, type MeView, type Tenant } from './api.ts';
import { onAccountSuspended } from './accountSuspension.ts';
import { Controls } from './Controls.tsx';
import { Documentation } from './Documentation.tsx';
import { Portfolio } from './Portfolio.tsx';
import { RestoreAccess } from './RestoreAccess.tsx';
import { AccountBadge } from './ClientRow.tsx';
import { Schedule } from './Schedule.tsx';
import { Settings } from './Settings.tsx';
import { Support } from './support/Support.tsx';
import { setSignInNotice } from './SignIn.tsx';
import { navigate, ADMIN_PATH, SIGN_IN_PATH } from './route.ts';
import { TenantDetail } from './TenantDetail.tsx';
import { TryIt } from './TryIt.tsx';
import { Users } from './Users.tsx';
import { getTheme, setTheme, subscribeTheme, type Theme } from './theme.ts';
import './shell.css';
// The sidebar's identity block lives with the rest of the sign-in feature
// rather than in shell.css, because it changes when that feature changes.
import './users.css';

/**
 * The console shell: sidebar, header, and the frame every screen renders in.
 * Rendered at `/app`; `/` is the public landing page and `/signin` is the form.
 *
 * Sections are grouped by how often an operator touches them rather than by the
 * order they were built. Operations is the day's work — the clients, what has
 * gone wrong on them, the sweep that keeps evidence current. Help is
 * consulted and left alone — the catalogue, the documentation, and Support
 * when neither answers the question. Admin is owner work and, for everybody,
 * Settings. Sandbox is the pipeline against a sample tenant,
 * which is the only thing that can be judged before an Entra registration
 * exists.
 *
 * Onboarding used to sit in Operations, listing the clients that had not
 * connected. It is two tabs on Clients now: the clients were never a different
 * list, and an operator had to know which screen a client was on before they
 * could look for them.
 *
 * Still no router. Six sections and a drill-down do not need one, and adding one
 * now would be scaffolding for a shape nobody has settled.
 */

type SectionId =
  | 'clients'
  | 'alerts'
  | 'schedule'
  | 'catalogue'
  | 'docs'
  | 'settings'
  | 'try'
  | 'setup'
  | 'estate'
  | 'sweep'
  | 'audit'
  | 'admin-people'
  | 'support';

interface NavItem {
  readonly id: SectionId;
  readonly label: string;
  /**
   * The capability this entry needs, or absent when everybody who can sign in
   * can use it. Read from the signed-in person's `capabilities` rather than
   * from their role: what a role may do is the server's to decide, and a table
   * of roles here would be a second copy free to drift out of step with it.
   *
   * **This is a convenience, not the control.** Every route behind every one of
   * these screens is authorised again on the server. Hiding an entry saves
   * somebody walking into a 403; it prevents nothing.
   */
  readonly requires?: Capability;
}

interface NavGroup {
  readonly id: string;
  readonly label: string;
  readonly items: readonly NavItem[];
}

const GROUPS: readonly NavGroup[] = [
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { id: 'clients', label: 'Clients' },
      { id: 'alerts', label: 'Alerts' },
      { id: 'schedule', label: 'Schedule' },
    ],
  },
  /*
   * Straight after Operations, and a group of its own rather than an Admin
   * item: Admin disappears for engineers and viewers, and every role in an
   * account can open and read its tickets. The catalogue and the documentation
   * sit above Support, so that the answer is one click nearer than the ticket
   * asking for it.
   */
  {
    id: 'help',
    label: 'Help',
    items: [
      { id: 'catalogue', label: 'Catalogue' },
      // No `requires`. Reading is not a capability anywhere in this product,
      // and a viewer who cannot run an assessment still needs to know what one
      // does before reading its findings.
      { id: 'docs', label: 'Documentation' },
      { id: 'support', label: 'Support' },
    ],
  },
  /**
   * Owner work, plus Settings. Every item but Settings requires
   * `deployment.manage`, which only an owner holds, so an engineer or a viewer
   * sees this group with Settings alone in it. Settings stays open to them
   * because its "Your account" tab — their own password and sessions — is for
   * everybody; its Branding and Delivery tabs are gated on `settings.write`
   * inside the screen. The gating here is a convenience, not the control: each
   * route behind these screens is authorised again on the server, and the
   * destructive ones (the manual sweep, the deployment schedule) refuse anyone
   * without the capability regardless of what the console renders. People
   * appears here as well as inside Settings, because managing accounts is owner
   * work too.
   *
   * "Client management" used to sit at the top of this group, listing the same
   * clients as the Clients screen and differing only in which verbs a row
   * carried — which is not something a menu label can say. It merged into
   * Clients, where each row action is gated on the capability it needs, so the
   * whole client list is one screen visible to every role.
   */
  {
    id: 'admin',
    label: 'Admin',
    items: [
      { id: 'estate', label: 'Estate', requires: 'deployment.manage' },
      { id: 'sweep', label: 'Sweep', requires: 'deployment.manage' },
      { id: 'audit', label: 'Audit log', requires: 'deployment.manage' },
      { id: 'admin-people', label: 'People', requires: 'deployment.manage' },
      { id: 'settings', label: 'Settings' },
    ],
  },
  { id: 'sandbox', label: 'Sandbox', items: [{ id: 'try', label: 'Try it' }] },
];

/**
 * Only while no Entra application is registered. A permanent banner on every
 * screen is shouting; a nav item that disappears when the work is done is the
 * same information with an end state.
 *
 * Shown to whoever can act on it. Registering an application and putting its
 * credentials in the server environment is deployment configuration, so a
 * viewer reading four steps they cannot carry out learns only that something is
 * wrong and that it is not theirs to fix.
 */
const SETUP_GROUP: NavGroup = {
  id: 'setup',
  label: 'Setup',
  items: [{ id: 'setup', label: 'Connect Entra', requires: 'settings.write' }],
};

/** The nav as this person sees it: groups whose every item is out of reach go too. */
const visibleGroups = (groups: readonly NavGroup[], me: MeView): NavGroup[] =>
  groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.requires === undefined || can(me, item.requires)),
    }))
    .filter((group) => group.items.length > 0);

interface Health {
  status: string;
  entra: string;
}

/**
 * Dismissal is stored against the state it was dismissed for, not as a boolean,
 * so the notice returns by itself if the deployment's Entra state changes. An
 * operator who does not know why nothing can connect concludes the product is
 * broken, and that conclusion must not be one click away permanently.
 */
const NOTICE_KEY = 'driftwatch-setup-notice-dismissed';

const readDismissed = (): string | null => {
  try {
    return window.localStorage.getItem(NOTICE_KEY);
  } catch {
    return null;
  }
};

const writeDismissed = (state: string): void => {
  try {
    window.localStorage.setItem(NOTICE_KEY, state);
  } catch {
    // Losing the dismissal across reloads is a far smaller cost than taking the
    // console down because a browser blocks storage.
  }
};

/**
 * Throw away the shared bearer token the console used to keep.
 *
 * It is dead weight to this build — nothing reads it — but it is a live
 * credential for the API sitting in `localStorage` in every browser that ever
 * signed in to the old console, readable by any script that gets injected.
 * Moving to a cookie the console cannot read achieves nothing while a copy of
 * the old one is still lying there, so every load removes it. It can go once no
 * browser can plausibly still be holding one.
 */
const forgetLegacyToken = (): void => {
  try {
    window.localStorage.removeItem('apiToken');
  } catch {
    // Storage refused. There is nothing to do and nothing worth breaking over.
  }
};

const SWEEP_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const formatSweep = (iso: string): string | null => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : SWEEP_FORMAT.format(at);
};

const findItem = (groups: readonly NavGroup[], id: SectionId): NavItem | null => {
  for (const group of groups) {
    const found = group.items.find((item) => item.id === id);
    if (found) return found;
  }
  return null;
};

const findGroup = (groups: readonly NavGroup[], id: SectionId): NavGroup | null =>
  groups.find((group) => group.items.some((item) => item.id === id)) ?? null;

/**
 * What an operator has to do outside DriftWatch before a tenant can be
 * connected. Numbered instructions rather than buttons: the product is
 * read-only, and every one of these steps happens in somebody else's console.
 */
const SetupPanel = () => (
  <div className="empty">
    <span className="empty-title">No Entra application is registered yet</span>
    <p className="empty-body">
      Client tenants cannot be connected or collected until this deployment has its own Entra
      application. The catalogue, stored reports and <em>Try it</em> all work meanwhile.
    </p>
    <ol className="empty-steps">
      <li>
        Register an application in the Microsoft Entra admin centre of your own tenant, with a
        redirect back to this console.
      </li>
      <li>
        Grant it the read-only Microsoft Graph application permissions DriftWatch asks for. It
        requests nothing that can change a client&rsquo;s tenant.
      </li>
      <li>
        Put the client id, client secret and redirect URI in the server environment, then restart
        the API.
      </li>
      <li>
        Reload this console. The Setup entry disappears and consent links become available on
        Clients.
      </li>
    </ol>
    <p className="note setup-doc">
      The full procedure is written up in <span className="mono">docs/ONBOARDING.md</span>.
    </p>
  </div>
);

interface AppearanceProps {
  theme: Theme;
  onChange: (theme: Theme) => void;
}

const Appearance = ({ theme, onChange }: AppearanceProps) => (
  <div className="appearance">
    <span className="label" id="appearance-label">
      Appearance
    </span>
    <div className="appearance-options" role="group" aria-labelledby="appearance-label">
      {(['light', 'dark'] as const).map((option) => (
        <button
          key={option}
          type="button"
          className="appearance-option"
          aria-pressed={theme === option}
          onClick={() => onChange(option)}
        >
          {option === 'light' ? 'Light' : 'Dark'}
        </button>
      ))}
    </div>
  </div>
);

const ROLE_TEXT: Record<string, string> = {
  owner: 'Owner',
  engineer: 'Engineer',
  viewer: 'Viewer',
};

interface IdentityProps {
  me: MeView;
  busy: boolean;
  onSignOut: () => void;
}

const Identity = ({ me, busy, onSignOut }: IdentityProps) => (
  <div className="identity">
    <span className="label" id="identity-label">
      Signed in
    </span>
    <div className="identity-row">
      <p className="identity-who" aria-labelledby="identity-label">
        <span className="identity-name">{me.displayName}</span>
        <span className="identity-role">{ROLE_TEXT[me.role] ?? me.role}</span>
      </p>
      <button
        type="button"
        className="btn btn-quiet btn-sm identity-out"
        aria-busy={busy || undefined}
        disabled={busy}
        onClick={onSignOut}
      >
        Sign out
      </button>
    </div>
  </div>
);

/**
 * Who is signed in, as far as the console knows.
 *
 * `loading` is a state rather than an assumption because the answer costs a
 * round trip: rendering the sign-in form while `/auth/me` is still in flight
 * flashes it at everybody who is already signed in, and a form that appears and
 * vanishes reads as a session that was lost and recovered.
 */
type Auth =
  | { readonly phase: 'loading' }
  | { readonly phase: 'out'; readonly notice: string | null }
  | { readonly phase: 'in'; readonly me: MeView };

export const App = () => {
  const [section, setSection] = useState<SectionId>('clients');
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [auth, setAuth] = useState<Auth>({ phase: 'loading' });
  const [signingOut, setSigningOut] = useState(false);
  const [health, setHealth] = useState<Health | null>(null);
  const [sweepAt, setSweepAt] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(readDismissed);
  const [theme, setThemeState] = useState<Theme>(getTheme);

  const me = auth.phase === 'in' ? auth.me : null;

  useEffect(() => subscribeTheme(setThemeState), []);

  useEffect(() => {
    forgetLegacyToken();
    void api
      .me()
      .then((user) => setAuth({ phase: 'in', me: user }))
      .catch((cause: unknown) =>
        setAuth({
          phase: 'out',
          // A 401 is the ordinary answer to "am I signed in": nothing has gone
          // wrong and there is nothing to explain. Anything else has, and the
          // sign-in attempt that follows would otherwise fail with no context.
          notice: isSignedOut(cause)
            ? null
            : 'The console could not reach the server to check your session.',
        }),
      );
  }, []);

  /**
   * Any call, on any screen, that comes back 401 mid-session. The shell is the
   * only component that can change what is on screen, so it is the one that
   * listens. Whatever the person was doing is discarded — but it is discarded
   * with a sentence saying why, because silently replacing a half-finished form
   * with a sign-in box reads as the console having crashed.
   */
  useEffect(
    () =>
      api.onSessionEnded(() =>
        setAuth({
          phase: 'out',
          notice:
            'Your session ended, so anything you had not saved was not sent. Sign in to carry on.',
        }),
      ),
    [],
  );

  /*
   * A call refused because billing has just suspended the account — the sweep
   * ends a trial on the hour, whoever is mid-screen. Asking `/auth/me` again
   * swaps the shell for Restore access rather than leaving every screen to
   * paint its own error.
   */
  useEffect(
    () =>
      onAccountSuspended(() => {
        void api
          .me()
          .then((user) => setAuth({ phase: 'in', me: user }))
          .catch(() => undefined);
      }),
    [],
  );

  useEffect(() => {
    if (me === null || me.restricted !== null) return;
    fetch('/api/healthz')
      .then((response) => (response.ok ? (response.json() as Promise<Health>) : null))
      .then(setHealth)
      .catch(() => setHealth(null));
  }, [me]);

  /**
   * Fetched once for the whole console rather than per screen. It is a header
   * ornament: if it fails it shows nothing, and it must never be able to block
   * or break the page underneath it.
   */
  useEffect(() => {
    if (me === null || me.restricted !== null) return;
    void api
      .getSchedule()
      .then((plan) => setSweepAt(plan.now))
      .catch(() => setSweepAt(null));
  }, [me]);

  /**
   * Nobody is signed in, so this shell has nothing to render and says so by
   * leaving. In an effect rather than during render because navigating is a
   * side effect on the document, and `replace` because a back button that
   * returns to a console which immediately ejects you again is worse than one
   * that goes back to where you actually came from.
   */
  useEffect(() => {
    if (auth.phase !== 'out') return;
    setSignInNotice(auth.notice);
    navigate(SIGN_IN_PATH, { replace: true });
  }, [auth]);

  const signOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await api.signOut();
      setAuth({ phase: 'out', notice: null });
    } catch {
      // The console drops to sign-in either way — leaving somebody inside a
      // shell they have asked to leave is worse — but it must not claim the
      // session was ended when the server never confirmed it was.
      setAuth({
        phase: 'out',
        notice:
          'You have been signed out in this browser, but the server did not confirm it. Close the browser to be certain.',
      });
    } finally {
      setSigningOut(false);
      setTenant(null);
      setSection('clients');
    }
  }, []);

  /**
   * `out` renders the same thing as `loading` rather than a screen of its own:
   * the effect above is already on its way to `/signin`, and painting an
   * explanation here would flash a second message beside the one the sign-in
   * screen is about to show for the same reason.
   */
  if (auth.phase !== 'in') {
    return (
      <div className="signin">
        <p className="note" role="status">
          Loading…
        </p>
      </div>
    );
  }

  /*
   * Billing has suspended the account: the one screen the server will still
   * serve, instead of a shell whose every section answers 403. Paying lifts
   * it, and `onRestored` swaps the full console back in without a reload.
   */
  if (auth.me.restricted === 'billing') {
    return (
      <RestoreAccess
        me={auth.me}
        busy={signingOut}
        onSignOut={() => void signOut()}
        onRestored={(next) => setAuth({ phase: 'in', me: next })}
      />
    );
  }

  const setupNeeded = health !== null && health.entra !== 'configured';
  /**
   * Whether a screen may offer work that needs the deployment's own Entra
   * application — creating a client, which is refused with a 503 until one
   * exists. An unanswered `/healthz` counts as configured for the same reason it
   * does not raise the Setup nav item: not knowing is not evidence of a gap, and
   * a screen that hides its primary action on a failed health check would be
   * broken by an unrelated outage.
   */
  const entraConfigured = !setupNeeded;
  const groups = visibleGroups(setupNeeded ? [SETUP_GROUP, ...GROUPS] : GROUPS, auth.me);

  // The Setup section vanishes the moment a registration appears; anyone
  // standing on it lands back on the clients they came for.
  const active: SectionId = section === 'setup' && !setupNeeded ? 'clients' : section;
  const item = findItem(groups, active) ?? GROUPS[0]!.items[0]!;
  const group = findGroup(groups, active) ?? GROUPS[0]!;
  const sweep = sweepAt === null ? null : formatSweep(sweepAt);
  const noticeOpen = setupNeeded && dismissed !== health.entra;

  const open = (next: SectionId) => {
    setTenant(null);
    setSection(next);
  };

  return (
    <div className="shell">
      <a className="skip-link" href="#content">
        Skip to content
      </a>

      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="sidebar-brand-name">DriftWatch</span>
        </div>

        <nav className="sidebar-nav" aria-label="Sections">
          {groups.map((entry) => (
            <div
              key={entry.id}
              className={entry.id === 'setup' ? 'nav-group nav-group-setup' : 'nav-group'}
              role="group"
              aria-labelledby={`nav-${entry.id}`}
            >
              <h2 className="label nav-heading" id={`nav-${entry.id}`}>
                {entry.label}
              </h2>
              <ul className="nav-list">
                {entry.items.map((navItem) => (
                  <li key={navItem.id}>
                    <button
                      type="button"
                      className="nav-item"
                      aria-current={navItem.id === active ? 'page' : undefined}
                      onClick={() => open(navItem.id)}
                    >
                      {navItem.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <p className="readonly-note">
            <strong>DriftWatch can only read.</strong>
            It holds no permission to change anything in a client&rsquo;s tenant.
          </p>

          {auth.me.platformOwner && (
            /*
             * Only DriftWatch's own staff see this, and seeing it is not what
             * grants it: every route behind `/admin` declares a requirement no
             * customer role can hold. Placed in the footer rather than in the
             * section list because it leaves the console entirely — it is a
             * different document, with a different meaning for the word client.
             */
            <button
              type="button"
              className="link sidebar-platform"
              onClick={() => navigate(ADMIN_PATH)}
            >
              Platform administration
            </button>
          )}

          <Identity me={auth.me} busy={signingOut} onSignOut={() => void signOut()} />

          <Appearance theme={theme} onChange={setTheme} />

          <p className="sidebar-meta">Entra, Intune and data exposure</p>
        </div>
      </aside>

      <div className="shell-body">
        <header className="topbar">
          <div className="topbar-inner">
            <div>
              <p className="crumb">
                {tenant ? 'Clients' : group.label}
                <span className="crumb-sep" aria-hidden="true">
                  /
                </span>
                {tenant ? tenant.displayName : item.label}
              </p>
              <h1>{tenant ? tenant.displayName : item.label}</h1>
              {tenant?.accountName !== undefined && <AccountBadge name={tenant.accountName} />}
            </div>

            <div className="topbar-right">
              {sweep !== null && (
                <p className="sweep">
                  <span className="label">Sweep plan as at</span>
                  <span className="num">{sweep}</span>
                </p>
              )}
              <span className="chip-readonly">Read-only</span>
            </div>
          </div>
        </header>

        <main id="content">
          {noticeOpen && active === 'clients' && !tenant && (
            <div className="callout callout-caution setup-strip">
              <div>
                <span className="callout-title">No Entra application is registered yet</span>
                Client tenants cannot be connected or collected until one is. Open{' '}
                <strong>Setup</strong> in the sidebar for the four steps.
              </div>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => {
                  writeDismissed(health.entra);
                  setDismissed(health.entra);
                }}
              >
                Dismiss
              </button>
            </div>
          )}

          {tenant ? (
            <TenantDetail me={auth.me} tenant={tenant} onBack={() => setTenant(null)} />
          ) : (
            <>
              {active === 'setup' && <SetupPanel />}
              {active === 'clients' && (
                <Portfolio me={auth.me} onOpen={setTenant} entraConfigured={entraConfigured} />
              )}
              {active === 'alerts' && <Alerts />}
              {active === 'schedule' && <Schedule />}
              {active === 'catalogue' && <Controls />}
              {active === 'docs' && <Documentation />}
              {active === 'settings' && <Settings me={auth.me} />}
              {active === 'try' && <TryIt />}
              {active === 'estate' && <Estate onOpen={setTenant} />}
              {active === 'sweep' && <Sweep me={auth.me} />}
              {active === 'audit' && <Audit />}
              {active === 'admin-people' && <Users me={auth.me} />}
              {active === 'support' && <Support />}
            </>
          )}
        </main>
      </div>
    </div>
  );
};
